import {
	Table,
	TableHead,
	TableBody,
	TableCell,
	TableRow,
	TableFooter,
	TablePagination,
	Box,
	Typography
} from "@mui/material";
import { useEffect, useState, type ChangeEvent, type MouseEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { TablePaginationActions } from "./Emails";
import getSearchParamsValue from "utils/getSearchParamsValue";
import Api from "api";
import type { ResGetTimeTracking } from "api/TimeTracking";

const COLS = [ "No", "Name", "Email", "Mission Times (seconds)" ];

function MissionTimeTrackingPage()
{
	const [ data, setData ] = useState<ResGetTimeTracking[]>([]);
	const [ isFetching, setIsFetch ] = useState<boolean>(false);
	const [ searchParams, setSearchParams ] = useSearchParams();
	const [ rowsPerPage, setRowsPerPage ] = useState<number>(getSearchParamsValue(searchParams, "rowsPerPage", "number") ?? 10);
	const [ page, setPage ] = useState<number>(() =>
	{
		const value = getSearchParamsValue(searchParams, "page", "number");

		return value === null ? 0 : value - 1;
	});

	useEffect(() =>
	{
		Api.timeTracking.getDataTimeTracking()
			.then(setData)
			.catch(console.error)
			.finally(() => setIsFetch(false));
	}, []);

	useEffect(() =>
	{
		const updatedParams = new URLSearchParams(searchParams);

		updatedParams.set("page", String(page + 1));
		updatedParams.set("rowsPerPage", String(rowsPerPage));
		setSearchParams(updatedParams);
	}, [ page, rowsPerPage, searchParams, setSearchParams ]);

	function handleChangePage(_event: MouseEvent<HTMLButtonElement> | null, newPage: number)
	{
		setPage(newPage);
		window.scrollTo({ top: 0, behavior: "smooth" });
	}

	function handleChangeRowsPerPage(event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>)
	{
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	}

	return (
		<Box>
			<Typography variant="h6" gutterBottom>Mission Time Tracking</Typography>
			<Table>
				<TableHead>
					<TableRow>
						{COLS.map((col) => (
							<TableCell key={col} sx={{ fontWeight: "bold" }}>{col}</TableCell>
						))}
					</TableRow>
				</TableHead>
				<TableBody>

					{isFetching === true &&
						(
							<TableRow>
								<TableCell colSpan={COLS.length} sx={{ fontSize: "18px", textAlign: "center" }}>
									Loading...
								</TableCell>
							</TableRow>
						)
					}

					{(rowsPerPage > 0
						? data.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
						: data
					).map((row, i) => (
						<TableRow key={row.userId}>
							<TableCell>{i + 1 + page * rowsPerPage}</TableCell>
							<TableCell>{row.userName}</TableCell>
							<TableCell>{row.userEmail}</TableCell>
							<TableCell>
								<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
									{Object.entries(row.missions).map(([ mission, seconds ]) => (
										<Box key={mission}>
											<strong>{mission}:</strong>{" "}
											{seconds.totalActiveSeconds}s, started:{" "}
											{seconds.startTime ? new Date(seconds.startTime).toLocaleString() : "N/A"}
										</Box>
									))}
								</Box>
							</TableCell>
						</TableRow>
					))}
				</TableBody>
				<TableFooter>
					<TableRow>
						<TablePagination
							rowsPerPageOptions={[ 10, 15, 25, 50, { label: "All", value: -1 } ]}
							colSpan={COLS.length}
							count={data.length}
							rowsPerPage={rowsPerPage}
							page={page}
							SelectProps={{ inputProps: { "aria-label": "rows per page" }, variant: "standard" }}
							onPageChange={handleChangePage}
							onRowsPerPageChange={handleChangeRowsPerPage}
							ActionsComponent={TablePaginationActions}
						/>
					</TableRow>
				</TableFooter>
			</Table>
		</Box>
	);
}

export default MissionTimeTrackingPage;
